<template>
  <div style="overflow-y: auto; scrollbar-gutter: stable; max-height: 600px;">
    <div v-if="!distanceSensors.length" class="step-input">
      <p>No sensors need a distance unit.</p>
    </div>
    <div v-else class="distance-unit-section">
      <div class="distance-unit-header d-flex justify-content-between align-items-center">
        <h5>{{ currentDescription }} {{ activeIdx + 1 }} of {{ distanceSensors.length }}: {{ sensorName(currentSensor) }}</h5>

        <div class="button-group d-flex">
          <button class="btn btn-sm btn-outline-secondary" :disabled="!canGoPrev" @click="goPrev">
            Previous
          </button>
          <button class="btn btn-sm btn-outline-secondary ms-2" :disabled="!canGoNext" @click="goNext">
            Next Sensor
          </button>
        </div>
      </div>

      <p class="text-muted">Choose the unit this sensor's raw {{ currentDescription.toLowerCase() }} reading should be shown in.</p>

      <div class="attr-row">
        <label class="attr-label">Reading Unit</label>
        <select class="form-control-sm attr-input" :value="currentUnit" @change="setUnit($event.target.value)">
          <option v-if="needsUnit(currentSensor)" value="" disabled>Select a unit</option>
          <option v-for="unit in distanceUnits" :key="unit.key" :value="unit.key">{{ unitLabel(unit) }}</option>
        </select>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, ref} from "vue";
import {useStore} from "vuex";
import {DEFAULT_DISTANCE_UNIT} from "@/store/ConfigWizard.module.js";

const store = useStore();
const distanceUnits = computed(() => store.state.ConfigWizard.distanceUnits);
const volumeUnitKeys = computed(() => new Set(store.state.ConfigWizard.volumeUnits.map(u => u.key)));
const activeIdx = ref(0);

const distanceSensors = computed(() => store.getters['ConfigWizard/distanceSensors']);

const currentEntry = computed(() => distanceSensors.value[activeIdx.value]);
const currentSensorIdx = computed(() => currentEntry.value?.sensorIdx);
const currentSensor = computed(() => currentEntry.value?.sensor);
const currentDescription = computed(() => currentEntry.value?.register?.description || 'Distance');
// A pressure sensor reads the height of water above it, so its units are water columns, saved as eg inH2O
const water = computed(() => currentEntry.value?.readsLevel === true);
const unitLabel = (unit) => water.value ? `${unit.label} of water (${unit.key}H2O)` : unit.label;
const savedUnit = (sensor) => sensor?.distance_unit?.replace(/H2O$/, '');

const canGoPrev = computed(() => activeIdx.value > 0);
const canGoNext = computed(() => activeIdx.value < distanceSensors.value.length - 1);
const goPrev = () => { if (canGoPrev.value) activeIdx.value--; };
const goNext = () => { if (canGoNext.value) activeIdx.value++; };

// A sensor switched from Volume back to raw still has its volume unit and formula saved, so it needs a unit picked
const needsUnit = (sensor) => !sensor?.distance_config?.unit && volumeUnitKeys.value.has(sensor?.distance_unit);

const currentUnit = computed(() => {
  if (needsUnit(currentSensor.value)) return '';
  return currentSensor.value?.distance_config?.unit || savedUnit(currentSensor.value) || DEFAULT_DISTANCE_UNIT;
});
const setUnit = (unitKey) => {
  store.commit('ConfigWizard/setSensorDistanceUnit', {
    sensorIdx: currentSensorIdx.value,
    unit: unitKey,
  });
};

const sensorName = (sensor) => {
  // Attribute 9150 is the Sensor Name
  let name = sensor?.attributes?.find(a => a.attribute_info_id === 9150)?.attribute_value;
  if (name != null) {
    return name;
  }
  if (sensor?.sensor_type_name == null || sensor?.modbus_id == null) {
    return 'Unnamed Sensor';
  }
  return `${sensor.sensor_type_name} ${sensor.modbus_id}`;
};

const validate = () => {
  for (let i = 0; i < distanceSensors.value.length; i++) {
    const {sensor} = distanceSensors.value[i];
    if (needsUnit(sensor)) {
      activeIdx.value = i;
      return {valid: false, msg: `Please select a reading unit for ${sensorName(sensor)}.`};
    }
  }
  return {valid: true, msg: ''};
};
const save = async () => await store.dispatch('ConfigWizard/saveDistanceUnits');

// eslint-disable-next-line no-undef
defineExpose({validate, save});
</script>

<style scoped>
.distance-unit-header {
  margin-bottom: 10px;
}

.distance-unit-header h5 {
  margin: 0;
}

.button-group {
  display: flex;
  gap: 10px;
}

.text-muted {
  color: var(--color-txt) !important;
}

.attr-row {
  background-color: var(--color-bg);
  margin-bottom: 14px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 16px;
}

.attr-label {
  display: block;
  white-space: normal;
  margin-bottom: 6px;
}

.attr-input {
  text-align: center;
  width: 100%;
  background-color: #ffffff;
  color: #000000;
  border: 2px solid var(--color-border);
  border-radius: 4px;
  padding: 6px 8px;
}

.attr-input:focus {
  border-color: var(--active);
  outline: none;
  box-shadow: 0 0 0 2px var(--active);
}
</style>
